import type { Fragment, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';

/** Patch top-level replaced node localIds from replacement attrs. */
export function applyReplaceNodeLocalIdFix({
	tr,
	replacementContent,
	startPos,
}: {
	replacementContent: Fragment | null | undefined;
	startPos: number;
	tr: Transaction;
}): Transaction {
	if (!replacementContent || replacementContent.childCount === 0) {
		return tr;
	}

	const docSize = tr.doc.content.size;
	if (startPos < 0 || startPos > docSize) {
		return tr;
	}

	let cursor = findFirstBlockPos(tr, startPos, replacementContent.firstChild?.type);
	if (cursor === undefined) {
		return tr;
	}

	for (let i = 0; i < replacementContent.childCount; i++) {
		const replacementChild = replacementContent.child(i);
		if (cursor < 0 || cursor >= tr.doc.content.size) {
			break;
		}

		let resolved;
		try {
			resolved = tr.doc.resolve(cursor);
		} catch {
			break;
		}

		const docNode = resolved.nodeAfter;
		if (!docNode) {
			break;
		}

		// Types must match — if the slice landed in a way that makes the
		// shapes diverge we cannot reliably correlate `localId`s and should
		// not blindly rewrite the wrong node.
		if (docNode.type !== replacementChild.type) {
			cursor += docNode.nodeSize;
			continue;
		}

		const replacementLocalId: unknown = replacementChild.attrs?.localId;
		const currentLocalId: unknown = docNode.attrs?.localId;

		if (
			typeof replacementLocalId === 'string' &&
			replacementLocalId.length > 0 &&
			currentLocalId !== replacementLocalId &&
			docNode.attrs &&
			'localId' in docNode.attrs
		) {
			try {
				tr = tr.setNodeMarkup(
					cursor,
					undefined,
					{ ...docNode.attrs, localId: replacementLocalId },
					docNode.marks,
				);
			} catch {
				// Swallow — keep advancing so we still patch the remaining blocks.
			}
		}

		cursor += docNode.nodeSize;
	}

	return tr;
}

/**
 * `replaceRange` typically leaves `startPos` pointing inside the first
 * merged block (e.g. at the start of its text content). Walk up the
 * resolved position to find the opening position of the first node matching
 * `firstChildType`.
 */
function findFirstBlockPos(
	tr: Transaction,
	startPos: number,
	firstChildType: PMNode['type'] | undefined,
): number | undefined {
	const docSize = tr.doc.content.size;
	let resolved;
	try {
		resolved = tr.doc.resolve(Math.min(Math.max(startPos, 0), docSize));
	} catch {
		return undefined;
	}

	for (let depth = resolved.depth; depth >= 0; depth--) {
		const nodeAtDepth = resolved.node(depth);
		if (!nodeAtDepth) {
			continue;
		}
		if (firstChildType && nodeAtDepth.type !== firstChildType) {
			continue;
		}
		if (depth === 0) {
			break;
		}
		return resolved.before(depth);
	}

	// Fall back to the resolved position so the caller's `nodeAfter` lookup
	// can still drive the walk when the first block already sits at this
	// boundary.
	return resolved.pos;
}
